import Sty from "modules/Sty";
import { clsx, styx } from "modules/Utils";
import { CSSProperties } from "react";
import Icon from "elements/Icon";
import Colors from "modules/Colors";
import dayjs from "dayjs";

interface StatProgressProps {
    className?: string;
    style?: CSSProperties;
    title?: string;
    value?: string | number;
    subtitle1?: string;
    value1?: string | number;
    subtitle2?: string;
    value2?: string | number;
    icon?: string;
    onClick?: () => void;
    displayType?: 'default' | 'currency' | 'date' | 'number'; // NOTE: default is no formatting
    totalDays?: number;
    remainingDays?: number;
}

const StatProgress = ({
    className,
    style,
    title,
    value,
    subtitle1,
    value1,
    subtitle2,
    value2,
    icon,
    onClick,
    displayType = 'default',
    totalDays,
    remainingDays,
}: StatProgressProps) => {
    const getProgressBarWidth = () => {
        switch (displayType) {
            case 'currency':
                return `${(Number(value1) / Number(value) * 100).toFixed(2)}%`;
            case 'date':
                const totalDaysNumber = Number(totalDays);
                const remainingDaysNumber = Number(remainingDays);
                const daysElapsed = totalDaysNumber ? totalDaysNumber - remainingDaysNumber : 0;
                return `${(daysElapsed / totalDaysNumber * 100).toFixed(2)}%`
            case 'number':
                return `${(Number(value1) / Number(value) * 100).toFixed(2)}%`
            default:
                return '50%'
        }
    }

    const getProgressStatMainValue = () => {
        switch (displayType) {
            case 'currency':
                return `${value?.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`
            case 'date':
                return value
            case 'number':
                return value?.toLocaleString()
            default:
                return value
        }
    }

    const getProgressStatSubValues = (subValue: string | number) => {
        switch (displayType) {
            case 'currency':
                return subValue ? `${subValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}` : '$0'
            case 'date':
                return dayjs(subValue).format('MMM, DD')
            default:
                return subValue
        }
    }
    const renderStatProgressHeader = () => {
        return (
            <div
                className="statProgressLeftSide"
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 4,
                    width: '100%'
                }}
            >
                <div className="statProgressTitleContent">
                    <div className="statProgressValue" style={Sty.text18bold}>
                        {title}
                    </div>
                    <div className="statProgressMax" style={Sty.text16grey}>
                        {getProgressStatMainValue()}
                    </div>
                </div>
                {icon &&
                    <Icon
                        className="statProgressIcon"
                        icon={icon}
                        size={24}
                        onClick={onClick}
                    />
                }
            </div>
        )
    }

    const renderProgressBar = () => {
        return (
            <div
                className="statProgressProgressBar"
                style={sty.statProgressProgressBar}
            >
                <div
                    className="statProgressProgressBarFill"
                    style={styx(
                        sty.statProgressProgressBarFill,
                        { width: getProgressBarWidth() }
                    )}
                />
            </div>
        )
    }

    const renderStatProgressFooter = () => {
        return (
            <div className="statProgressFooter" style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 4,
                width: '100%'
            }}>
                <div style={{ display: 'flex', gap: 4 }}>
                    <div className="statProgressFooterValue1" style={Sty.text16grey}>
                        {subtitle1}:
                    </div>
                    <div className="statProgressFooterValue2">
                        {getProgressStatSubValues(value1 || '')}
                    </div>
                </div>
                {subtitle2 &&
                    <div style={{ display: 'flex', gap: 4 }}>
                        <div className="statProgressFooterValue3" style={Sty.text16grey}>
                            {subtitle2}:
                        </div>
                        <div className="statProgressFooterValue4">
                            {getProgressStatSubValues(value2 || '')}
                        </div>
                    </div>
                }
            </div>
        )
    }

    return (
        <div
            className={clsx("statProgressElement", className)}
            style={styx(sty.statProgressElement, style)}
        >
            {renderStatProgressHeader()}
            {renderProgressBar()}
            {renderStatProgressFooter()}
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    statProgressElement: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        backgroundColor: 'white',
        borderRadius: 8,
        padding: 8,
    },
    statProgressValue: {
        fontSize: 16,
    },
    statProgressProgressBar: {
        width: '100%',
        height: 8,
        backgroundColor: Colors.greyLight,
        borderRadius: 4,
        overflow: 'hidden',
    },
    statProgressProgressBarFill: {
        backgroundColor: Colors.purple,
        borderRadius: 4,
        width: '50%',
        height: 8,
    }
}

export default StatProgress;